import React, { useState } from 'react';
import { Box, Button, FormControl, FormLabel, Input, Typography, Alert, Card, Stack, Link } from '@mui/joy';
import { LoginRounded, MenuBook } from '@mui/icons-material';
import MailAPI, { LoginRequest } from '../api.ts';
import { useNavigate } from 'react-router-dom';
import { useMail } from '../contexts/Mail.tsx';
import ApiGuideModal from '../components/email/ApiGuideNew.tsx';
import AnnouncementModal from '../components/AnnouncementModal.tsx';

const api = new MailAPI();

export default function Login() {
  const navigate = useNavigate();
  const { setMail, setAuthenticated } = useMail();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiGuideOpen, setApiGuideOpen] = useState(false);
  const [formData, setFormData] = useState<LoginRequest>({
    username: '',
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await api.login(formData);
      setMail(result.user.username);
      setAuthenticated(true); // Update authentication state
      navigate('/');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof LoginRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.surface',
        p: 2
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 4
        }}
      >
        <Stack spacing={3}>
          <Box textAlign="center">
            <Typography level="h2" component="h1">
              Sign In
            </Typography>
            <Typography level="body-md" color="neutral">
              Access your temporary mailbox
            </Typography>
          </Box>

          {error && (
            <Alert color="danger" variant="soft">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  placeholder="your.email@domain.com"
                  value={formData.username}
                  onChange={handleChange('username')}
                  disabled={loading}
                />
              </FormControl>

              <FormControl required>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  disabled={loading}
                />
              </FormControl>

              <Button
                type="submit"
                loading={loading}
                endDecorator={<LoginRounded />}
                fullWidth
                sx={{ mt: 2 }}
              >
                Sign In
              </Button>
            </Stack>
          </form>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link
              component="button"
              onClick={() => setApiGuideOpen(true)}
              startDecorator={<MenuBook />}
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              View API Documentation
            </Link>
          </Box>
        </Stack>
      </Card>

      <AnnouncementModal />
      <ApiGuideModal open={apiGuideOpen} onClose={() => setApiGuideOpen(false)} />
    </Box>
  );
}
